'use client';

import { AppImageClient } from '@components/appImage';
import { Card } from '@components/card';
import { ChipWithLinearGradient } from '@components/chipWithLinearGradient';
import { PageSection } from '@components/pageSection';

import { AppsUsageSectionProps } from './types';

import { useIsMobile } from '@/shared/hooks';

/**
 * Секция отображения приложений, использующих определенный API или сервис
 *
 * Отображает заголовок, описание, набор чипов с категориями и изображение.
 * Адаптивная верстка: на мобильных устройствах элементы располагаются вертикально,
 * на десктопе - в виде сетки с двумя колонками.
 *
 * @example
 * ```tsx
 * <AppsUsageSection
 *   title="Приложения с динамическими картами"
 *   chips={[
 *     { id: 1, label: 'Логистика', orderForMobile: 1 },
 *     { id: 2, label: 'Туризм', orderForMobile: 2 }
 *   ]}
 *   imageSrc="/images/apps-usage.png"
 *   imageAlt="Примеры приложений"
 * />
 * ```
 */
export const AppsUsageSection = ({
  title,
  description,
  chips,
  imageSrc,
  imageData,
  imageAlt,
  imageNode,
  titleVariant,
  tagsMaxWidth,
}: AppsUsageSectionProps) => {
  const isMobile = useIsMobile();

  return (
    <PageSection
      title={title}
      description={description}
      titleVariant={titleVariant}
    >
      <div className="px-4">
        <Card>
          <div className="grid grid-cols-[auto_300px] max-sm:flex max-sm:flex-col max-sm:gap-6 max-sm:p-5 max-sm:pb-0">
            <div className="py-8 pl-9 max-sm:p-0">
              <div
                className="flex flex-row flex-wrap gap-2"
                style={{ maxWidth: tagsMaxWidth ?? '740px' }}
              >
                {chips.map(({ id, label, orderForMobile, orderForWeb }) => (
                  <div
                    key={id}
                    style={{ order: isMobile ? orderForMobile : orderForWeb }}
                  >
                    <ChipWithLinearGradient className="md:text-nowrap">
                      {label}
                    </ChipWithLinearGradient>
                  </div>
                ))}
              </div>
            </div>
            {imageNode ?? (
              <div className="max-sm:b-0 flex pt-8 pr-9 pl-0 max-sm:p-5">
                {imageData ? (
                  <AppImageClient {...imageData} alt={imageAlt} />
                ) : (
                  <img src={imageSrc} alt={imageAlt} />
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </PageSection>
  );
};
